import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";

// Backend-served pictures (kiosk photo, QR) can fail once on a cold start or a slow asset proxy — never leave a broken placeholder:
// retry once with a cache-buster, then fall back to the bundled copy (or hide the image when there is none).
function ResilientImg({ src, fallback = null, hideOnFail = false, ...rest }) {
  const [state, setState] = useState({ src, tries: 0, hidden: false });
  if (state.hidden) return null;
  const onError = () => {
    if (state.tries === 0) setTimeout(() => setState({ src: `${src}${src.includes("?") ? "&" : "?"}r=${Date.now()}`, tries: 1, hidden: false }), 1500);
    else if (state.tries === 1 && fallback) setState({ src: fallback, tries: 2, hidden: false });
    else if (hideOnFail) setState((s) => ({ ...s, hidden: true }));
  };
  return <img src={state.src} onError={onError} {...rest} />;
}

const ACCENT = {
  yellow: { border: "hover:border-neon-yellow/80", badge: "border-neon-yellow/60 text-neon-yellow", dot: "bg-neon-yellow", tag: "text-neon-yellow", check: "text-neon-yellow", btn: "bg-neon-yellow", glow: "group-hover:shadow-[0_0_60px_-20px_rgba(255,215,0,0.45)]" },
  cyan: { border: "hover:border-neon-cyan/80", badge: "border-neon-cyan/60 text-neon-cyan", dot: "bg-neon-cyan", tag: "text-neon-cyan", check: "text-neon-cyan", btn: "bg-neon-cyan", glow: "group-hover:shadow-[0_0_60px_-20px_rgba(0,229,255,0.45)]" },
};

// A live campaign Arcade Ops built and is running — shown in the hero as proof of the engine.
export default function LiveCampaignCard({ id, index, accent = "yellow", title, tag, pitch, highlights = [], image, imageFallback = null, imageAlt, imagePosition = "center", qr = null, href, viewTestId, delay = 0 }) {
  const a = ACCENT[accent];
  return (
    <article data-testid={id} className={`group relative flex flex-col border border-border/70 bg-[#121214]/80 backdrop-blur-sm transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 ${a.border} ${a.glow} rise`} style={{ animationDelay: `${delay}ms` }}>
      <a href={href} className="relative block aspect-[4/5] sm:aspect-[3/4] overflow-hidden bg-black" aria-label={`Open ${title} campaign page`}>
        <ResilientImg src={image} fallback={imageFallback} alt={imageAlt} loading="lazy" data-testid={`${id}-image`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" style={{ objectPosition: imagePosition }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/10" />
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/75 border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.24em] ${a.badge}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full animate-pulse ${a.dot}`} /> Live campaign #{index}
        </span>
        {qr && (
          <span className="absolute bottom-3 right-3 flex flex-col items-center gap-1">
            <ResilientImg src={qr} hideOnFail alt={`QR code for ${title}`} loading="lazy" data-testid={`${id}-qr`} className="w-16 h-16 bg-white p-1" />
            <span className="font-mono text-[8px] uppercase tracking-widest text-white/70">scan</span>
          </span>
        )}
        <div className={`absolute bottom-3 left-3 ${qr ? "right-24" : "right-3"}`}>
          <div className="font-heading font-black text-xl sm:text-2xl text-white leading-tight">{title}</div>
          <div className={`mt-0.5 font-mono text-[10px] uppercase tracking-widest ${a.tag}`}>{tag}</div>
        </div>
      </a>
      <div className="flex flex-col flex-1 p-4 sm:p-5 gap-3">
        <p className="text-sm text-white/75 leading-relaxed">{pitch}</p>
        <ul className="space-y-1.5">
          {highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-xs sm:text-[13px] text-white/85"><Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.check}`} /> {h}</li>
          ))}
        </ul>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          <a href={href} data-testid={viewTestId} className={`inline-flex items-center gap-1.5 h-10 px-4 text-black font-heading font-bold text-xs uppercase tracking-wide hover:bg-white transition-colors ${a.btn}`}>
            View campaign page <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <a href="#inquire" data-testid={`${id}-run-one`} className="inline-flex items-center h-10 px-3 border border-border text-white/80 hover:border-white hover:text-white transition-colors font-mono text-[10px] uppercase tracking-widest">
            Run one like this
          </a>
        </div>
      </div>
    </article>
  );
}
